export function normalizeApiBase(apiUrl) {
  const source = String(apiUrl || '').trim().replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(source)) throw new Error('Provide full API origin, e.g. https://api.example.com');
  return source;
}

export async function supportApi({ apiUrl, token, path, method = 'GET', body }) {
  if (!token) throw new Error('Admin JWT missing; log in to your admin account.');
  const base = normalizeApiBase(apiUrl);
  const isForm = body instanceof FormData;
  const response = await fetch(`${base}/api/support${path}`, {
    method,
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${String(token).replace(/^Bearer\s+/i, '')}`,
      ...(!isForm && body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: isForm ? body : JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || payload.msg || `HTTP ${response.status}`);
  return payload;
}

export function mergeMessages(previous, incoming) {
  const map = new Map();
  [...previous, ...incoming].forEach(message => map.set(message._id, message));
  return [...map.values()].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() ||
    a._id.localeCompare(b._id)
  );
}

export const formatTime = date => new Date(date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

// JWT-authorized private attachment; progress reflects downloaded response bytes.
export async function fetchSupportMedia({ apiUrl, token, messageId, signal, onProgress, voiceMp3 = false }) {
  if (!token) throw new Error('Admin JWT missing; log in again.');
  const base = normalizeApiBase(apiUrl);
  const response = await fetch(`${base}/api/support/media/${encodeURIComponent(messageId)}/content${voiceMp3 ? '?format=mp3' : ''}`, {
    cache: 'no-store',
    headers: { Authorization: `Bearer ${String(token).replace(/^Bearer\s+/i, '')}` }, signal,
  });
  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    throw new Error(result.error || `Unable to load media (HTTP ${response.status})`);
  }
  if (!onProgress || !response.body) return response.blob();
  const declared = Number(response.headers.get('Content-Length'));
  const total = Number.isFinite(declared) && declared > 0 ? declared : null;
  const reader = response.body.getReader();
  let received = 0;
  const chunks = [];
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value); received += value.byteLength;
        onProgress(received, total);
      }
    }
    if (total !== null && received !== total) throw new Error('Incomplete audio download. Retry.');
    return new Blob(chunks, { type: response.headers.get('Content-Type') || 'application/octet-stream' });
  } finally { reader.releaseLock(); }
}
